import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type {
  PublicStepConfig,
  PublicStepTypes,
  SdkError,
  SdkHandle,
  SdkResponse,
  SupportedLanguages,
} from 'onfido-sdk-ui';
import { useIntl } from 'react-intl';
import { useSegment } from '@repo/poly-hooks/segment';
import { device, isWebView } from '../../utils/device';
import { type OnfidoEvent, useOnfidoEvents } from './use-onfido-events';
import { useOnfidoObserver } from './use-onfido-observer';
import type { OnfidoModes } from './onfido.types';

const ONFIDO_ID = 'onfido-wrapper';

export interface OnfidoProps {
  onError: (error: SdkError) => void;
  onComplete: (response: SdkResponse) => void;
  mode: OnfidoModes;
  legalCountryCode: string;
  token: string;
  phoneNumber: string;
  onStepChange?: (step: 'first' | 'other' | 'last') => void;
}

export function Onfido({
  onError,
  onComplete,
  mode,
  legalCountryCode,
  token,
  phoneNumber,
  onStepChange,
}: OnfidoProps): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const [sdkHandle, setSdkHandle] = useState<SdkHandle | null>(null);
  const intl = useIntl();
  const segment = useSegment();
  const isVendorApple = device?.vendor === 'Apple';
  const isItaly = legalCountryCode === 'IT';

  const getSteps = (): (PublicStepTypes | PublicStepConfig)[] => {
    if (mode === 'onfido_standalone') {
      return [
        'welcome',
        {
          type: 'document',
          options: {
            forceCrossDevice: isItaly,
            documentTypes: {
              passport: { country: null },
              driving_licence: isItaly ? { country: null } : false,
              national_identity_card: { country: null },
              residence_permit: { country: null },
            },
          },
        },
        {
          type: 'face',
          options: {
            requestedVariant: 'video',
            uploadFallback: true,
          },
        },
        'complete',
      ];
    }

    return [
      {
        type: 'document',
        options: {
          forceCrossDevice: isItaly,
          documentTypes: {
            passport: { country: null },
            driving_licence: isItaly ? { country: null } : false,
            national_identity_card: { country: null },
            residence_permit: { country: null },
          },
        },
      },
      {
        type: 'face',
        options: {
          requestedVariant: isVendorApple && isWebView ? 'standard' : 'video',
          uploadFallback: true,
        },
      },
    ];
  };
  const steps = getSteps();
  const messages = intl.messages;

  const extractTranslations = (prefix: string): Record<string, string> => {
    const keys = Object.keys(messages).filter(key => key.startsWith(prefix));

    return keys.reduce<Record<string, string>>((collector, currentKey) => {
      collector[currentKey.replace(prefix, '')] = intl.formatMessage(
        {
          // oxlint-disable-next-line @repo/no-dynamic-intl-id
          id: currentKey,
        },
        { legalCountry: legalCountryCode },
        {
          ignoreTag: true,
        }
      );

      return collector;
    }, {});
  };

  useEffect(() => {
    let handle: SdkHandle | null;
    void (async () => {
      const onfido = await import('onfido-sdk-ui');

      // Because of the async nature of the `await import`, on test, it leads to flakiness.
      // By short-circuiting it here, we avoid the problem, if the div below no longer exists
      // do not try to init onfido.
      if (!document.getElementById(ONFIDO_ID)) return;

      handle = onfido.init({
        token,
        containerId: ONFIDO_ID,
        language: {
          // oxlint-disable-next-line typescript/consistent-type-assertions
          locale: intl.locale as SupportedLanguages,
          phrases: extractTranslations(`identity-verification-kit.qonto-onfido.${mode}.desktop.`),
          mobilePhrases: extractTranslations(
            `identity-verification-kit.qonto-onfido.${mode}.phone.`
          ),
        },
        steps,
        smsNumberCountryCode: legalCountryCode || 'FR',
        userDetails: {
          smsNumber: phoneNumber,
        },
        onComplete: result => {
          segment.track(`${mode}_sucessfully_completed`);
          onComplete(result);
        },
        onError: error => {
          if (mode === 'onfido_standalone') {
            segment.track(`${mode}_token_expired`);
          }
          onError(error);
        },
        disableAnalytics: true,
      });

      setSdkHandle(handle);
      segment.track(`${mode}_started`);
    })();

    return () => {
      handle?.tearDown();
    };
    // oxlint-disable-next-line react/exhaustive-deps -- init only once
  }, []);

  const onEvent = useCallback(
    (event: OnfidoEvent) => {
      if (!sdkHandle) {
        return;
      }

      if (mode !== 'onboarding_onfido') {
        return;
      }

      const firstStep = event === 'DOCUMENT_CAPTURE_FRONT';

      onStepChange?.(firstStep ? 'first' : 'other');
    },
    [mode, onStepChange, sdkHandle]
  );

  useOnfidoEvents(mode, onEvent);

  useOnfidoObserver(ref, mode, Boolean(sdkHandle), onStepChange);

  return <div data-test-onfido-widget id={ONFIDO_ID} ref={ref} />;
}
